import React from "react";
import ForgeShell, { ControlLabel, Segmented, Chips, Textarea, OutputCard, CopyableText, EmptyState } from "./ForgeShell";
import { useForgeModule, GenerateButton, ActionRow, DraftsList } from "./useForgeModule";

const FLOW_TYPES = [
  { value: "welcome", label: "Welcome" },
  { value: "launch", label: "Launch" },
  { value: "recovery", label: "Recovery" },
  { value: "nurture", label: "Nurture" },
  { value: "upsell", label: "Upsell" },
];
const TONES = ["Warm", "Direct", "Premium", "Story-led"];

export default function EmailCampaigns() {
  const m = useForgeModule("email");
  const i = m.inputs;
  const set = (k, v) => m.setInputs({ ...i, [k]: v });
  const emails = m.output?.emails || [];

  return (
    <ForgeShell
      testid="forge-email-campaigns"
      left={
        <>
          <ControlLabel>Sequence type</ControlLabel>
          <Segmented testid="email-sequence-type" options={FLOW_TYPES} value={i.sequence_type || "welcome"} onChange={(v) => set("sequence_type", v)} />

          <ControlLabel>Product / context</ControlLabel>
          <Textarea testid="email-context" value={i.context} onChange={(v) => set("context", v)} rows={3}
            placeholder="$99 Creator Growth Playbook — first-touch new subscribers" />

          <ControlLabel>Tone</ControlLabel>
          <Chips testid="email-tone" options={TONES} value={i.tone || "Direct"} onChange={(v) => set("tone", v)} />

          <ControlLabel hint="Goal of the sequence">Outcome</ControlLabel>
          <Textarea testid="email-outcome" value={i.outcome} onChange={(v) => set("outcome", v)} rows={2}
            placeholder="Move from cold subscriber → first purchase in 7 days" />

          <GenerateButton testid="email-generate" loading={m.loading} onClick={m.generate} />
        </>
      }
      center={
        !m.output ? (
          <EmptyState title="Generate a lifecycle sequence" body="A sequence with day-by-day subject, preview, body and CTA — visualised as a timeline." />
        ) : (
          <>
            <ActionRow testid="email-actions" onRegenerate={m.generate} onSave={() => m.saveDraft(m.output.sequence_name)} regenerating={m.loading} />

            <OutputCard title={m.output.sequence_name || "Sequence"}>
              <ol className="relative pl-6" data-testid="email-timeline">
                <span className="absolute top-2 bottom-2 left-2 w-px bg-line" />
                {emails.map((e, idx) => (
                  <li key={idx} className="relative mb-5 last:mb-0" data-testid={`email-step-${idx}`}>
                    <span className="absolute -left-[18px] top-1 h-3 w-3 rounded-full ashra-gradient-bg ring-4 ring-white" />
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="pill pill-ai text-[10px]">Day {e.day}</span>
                      <span className="text-[11px] text-meta">{e.cta}</span>
                    </div>
                    <p className="font-display font-bold text-ink text-sm" data-testid={`email-subject-${idx}`}>{e.subject}</p>
                    <p className="text-[11px] text-meta-soft italic mt-0.5">{e.preview}</p>
                    <div className="mt-2"><CopyableText testid={`email-body-${idx}`} text={e.body} /></div>
                  </li>
                ))}
              </ol>
            </OutputCard>
          </>
        )
      }
      right={
        <>
          <div>
            <ControlLabel>Flow types</ControlLabel>
            <ul className="mt-2 space-y-1 text-[11px] text-ink-soft">
              <li><span className="text-ai font-semibold">Welcome</span> · first 7 days of subscription</li>
              <li><span className="text-ai font-semibold">Launch</span> · pre-launch → close</li>
              <li><span className="text-ai font-semibold">Recovery</span> · abandoned / failed</li>
              <li><span className="text-ai font-semibold">Nurture</span> · ongoing value</li>
              <li><span className="text-ai font-semibold">Upsell</span> · post-purchase expansion</li>
            </ul>
          </div>
          <DraftsList drafts={m.drafts} onLoad={m.loadDraft} onDelete={m.deleteDraft}
            onToggleFavorite={m.toggleFavorite} onArchive={m.archiveDraft}
            onDuplicate={m.duplicateDraft} module="email" />
        </>
      }
    />
  );
}
